import { useState } from "react";
import { getAuthHeader } from "../../../utils/getAuthHeader";
import ConfirmModal from "../ConfirmModal";
import { API_BASE_ADMIN, showErrorToast } from "./types";

interface Props {
  onSuccess: () => void;
}

interface SyncState {
  farmId: string;
  deviceId: string;
  showModal: boolean;
  syncing: boolean;
}

export default function TriggerSyncCard({ onSuccess }: Props) {
  const [state, setState] = useState<SyncState>({
    farmId: "",
    deviceId: "",
    showModal: false,
    syncing: false,
  });

  const set = (patch: Partial<SyncState>) => setState((s) => ({ ...s, ...patch }));

  const handleSubmit = () => {
    if (!state.farmId.trim() || !state.deviceId.trim()) {
      showErrorToast("Farm ID and Device ID are required.");
      return;
    }
    set({ showModal: true });
  };

  const confirmSync = async () => {
    set({ showModal: false, syncing: true });
    try {
      const res = await fetch(
        `${API_BASE_ADMIN}/iot_endpoints/iot-farm-mappings`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
          body: JSON.stringify({ farm_id: state.farmId.trim(), device_id: state.deviceId.trim() }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as any).message || `Request failed (${res.status})`);
      setState({ farmId: "", deviceId: "", showModal: false, syncing: false });
      onSuccess();
    } catch (err: any) {
      showErrorToast(err.message || "Sync failed. Please try again.");
      set({ syncing: false });
    }
  };

  return (
    <>
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <p className="fw-semibold mb-3 text-muted" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Trigger Sync
          </p>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold" style={{ fontSize: "0.82rem" }}>Farm ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. FARM-0042"
                value={state.farmId}
                onChange={(e) => set({ farmId: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold" style={{ fontSize: "0.82rem" }}>Device ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. 7831"
                value={state.deviceId}
                onChange={(e) => set({ deviceId: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div className="col-12 col-md-auto">
              <button
                className="btn d-flex align-items-center gap-2"
                style={{ backgroundColor: "#6D9300", color: "white", borderColor: "#6D9300" }}
                onClick={handleSubmit}
                disabled={state.syncing}
              >
                {state.syncing ? (
                  <><span className="spinner-border spinner-border-sm" /> Syncing…</>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                      <polyline points="1 4 1 10 7 10" />
                      <path d="M3.51 15a9 9 0 1 0 .49-3.64" />
                    </svg>
                    Trigger Sync
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {state.showModal && (
        <ConfirmModal
          title="Confirm Sync"
          message={
            <>
              Are you sure you want to sync{" "}
              <strong className="text-dark">Farm ID: {state.farmId}</strong> with{" "}
              <strong className="text-dark">Device ID: {state.deviceId}</strong>?
            </>
          }
          confirmLabel="Confirm & Sync"
          onConfirm={confirmSync}
          onCancel={() => set({ showModal: false })}
        />
      )}
    </>
  );
}
